import '../../../stylesheets/dashboards/dashboards.css'
import '../../../stylesheets/dashboards/leaderboard.css'
import { useState, useEffect, useRef }                          from 'react'
import SummaryStats                                             from './SummaryStats.js'
import SummaryStatsBarLayout                                    from './SummaryStatsBarLayout.js'
import MoversGridLayout                                         from './MoversGridLayout.js'
import MoversBarLayout                                          from './MoversBarLayout.js'
import { usePublicAPI }                                         from '../../../hooks/external/proprietary/usePublicAPI.js';
import { assembleCPDActiveKeys, resolveStackedClassForColor,
         resolveChangeSignage                               }   from '../../../services/leaderboard/main/main.js';
import { formatNumber, formatNumberTwoDecimal,
         formatPercent, formatDollar }                          from '../../../utilities/formatNumber.js';
import { filterManyByIds, findOneById }                         from '../../../utilities/idResolution.js';


const Main = (props) => {
    const { cosPrices, indsPrices, compsCategories, selectedCompsCategoryId, selectedCompanyIds } = props

    // **====== Hooks ======** //
    const { getCoLogo } = usePublicAPI();

    // **======= State =======** //
    // -- Lifted React State - Read Only -- 

    // -- Local React State -- 
    /* 
        {
          id: 21,
          ticker: 'GOOG',
          name: 'Alphabet',
          priceCurrent: 352.93,
          priceT1: 344.41,
          priceT30: 338.2,
          priceT365: 252.88,
          oneDayPercentChange: 2.4737957666734367,
          oneDayNominalChange: 8.519999999999982,
          oneDayHeat: true,
          oneMonthPercentChange: 4.355410999408638,
          oneMonthNominalChange: 14.730000000000018,
          oneMonthHeat: true,
          oneYearPercentChange: 39.56422018348624,
          oneYearNominalChange: 100.05000000000001,
          oneYearHeat: true
        }
    */    
    const [period, setPeriod]           = useState('T1');
    const [type, setType]               = useState('percent');  
    const [gridLayout, setGridLayout]   = useState(true)   
    const [barLayout, setBarLayout]     = useState(false)
    
    // ** Local Variables **
    // NOTE: sits below the gate on purpose because prices is still null/stale until the gate opens/re-opens
    const cpdActiveFields            = assembleCPDActiveKeys(period, type)
    // NOTE: to copy array first w/ sort: prices is lifted context state and .sort() mutates in place
    // NOTE: N/A rows have no position on a numeric axis, so they get pushed to the end rather than compared
    const orderedCompanyPriceData    = [...cosPrices].sort((a, b) => {
        if (a[cpdActiveFields.change] === 'N/A') return 1
        if (b[cpdActiveFields.change] === 'N/A') return -1
        return b[cpdActiveFields.change] - a[cpdActiveFields.change]
    })

    // **=========== JSX Section Helper Functions ===========** //



    // **=========== JSX RETURN/RENDER SECTION: EVENTUALLY RESOLVED TO A PIECE OF RENDERED HTML TREE WHEN CODE TRANSPILED TO JS, AND JS CODE RUN ===========** //
    return (
        <div className="page-wrapper">
            <div className="dashboard">
                
                {/* 1. Left Panel - Controller */}                
                <div className="left-panel">

                    {/* Left Section 1: Header Section */}
                    <div className="left-panel-header-container">
                        <h2 className="panel-title">Selected Companies</h2>
                    </div>

                    {/* Left Section 2: Body Section */}
                    <div className="left-panel-body-container">
                        {/* Left Body Section 1: (Placeholder) for when we include the summary movement stats of whole sector vs SP vs NASDAQ */}                        
                        { gridLayout  && <SummaryStats
                            indsPrices              ={indsPrices}
                            priceDataActiveFields   ={cpdActiveFields}
                            period                  ={period}
                            type                    ={type}
                        /> }
                        { barLayout   && <SummaryStatsBarLayout
                            indsPrices              ={indsPrices}
                            priceDataActiveFields   ={cpdActiveFields}
                            period                  ={period}
                            type                    ={type}
                        /> }
                        {/* Left Body Section 2: Grid container rendering price movement stats with only grid cells for 'selected companies' - i.e. call out from what focused on from public comps*/}
                        { gridLayout  && <MoversGridLayout
                            orderedCompanyPriceData ={filterManyByIds(orderedCompanyPriceData, selectedCompanyIds)}
                            cpdActiveFields         ={cpdActiveFields}
                            period                  ={period}
                            type                    ={type}
                        /> }
                        { barLayout   && <MoversBarLayout
                            orderedCompanyPriceData ={filterManyByIds(orderedCompanyPriceData, selectedCompanyIds)}
                            cpdActiveFields         ={cpdActiveFields}
                            type                    ={type}
                        /> }
                    </div>   

                </div>

                {/* 2. Right Panel - Grid / Charts */}
                <div className="right-panel">

                    {/* Right Section 1: Header Section */}
                    <div className="right-panel-header-container">
                        {/* Node 1 of Header Section: the Title */}
                        <h2 className="panel-title">Movement</h2>
                        {/* Node 2 of Header Section with children 1, 2 and 3 being the Toggle Between Main Content Layouts (Grid / Bars) + Toggle Between 1 Day / 1 Month / 1 Year + Toggle Between % Change and Change Quantum} */}
                        <div className="right-panel-leaderboard-controls-container">
                            {/* Toggle 1 of 3: Main Content Layout Toggle grid/bars */}
                            <div className="layout-toggle">
                                <button
                                    className={`leaderboard-toggle-btn ${gridLayout ? 'active' : ''}`}
                                    onClick={() => {
                                        setGridLayout(true)
                                        setBarLayout(false)
                                    }}
                                >
                                    Grid
                                </button>
                                <button
                                    className={`leaderboard-toggle-btn ${barLayout ? 'active' : ''}`}
                                    onClick={() => {
                                        setGridLayout(false)
                                        setBarLayout(true)
                                    }}
                                >
                                    Bars
                                </button>
                            </div>

                            {/* Toggle 2 of 3: Period Change Toggle t1/t30/t365 */}
                            <div className="period-toggle">
                                <button 
                                    className={`leaderboard-toggle-btn ${period === 'T1' ? 'active' : ''}`}
                                    onClick={() => setPeriod('T1')}
                                >
                                    1 Day
                                </button>
                                <button 
                                    className={`leaderboard-toggle-btn ${period === 'T30' ? 'active' : ''}`}
                                    onClick={() => setPeriod('T30')}
                                >
                                    1 Month
                                </button>
                                <button 
                                    className={`leaderboard-toggle-btn ${period === 'T365' ? 'active' : ''}`}
                                    onClick={() => setPeriod('T365')}
                                >
                                    1 Year
                                </button>                      
                            </div>                         

                            {/* Toggle 3 of 3: Type of Change Toggle %/$ */}
                            <div className="type-toggle">
                                <button 
                                    className={`leaderboard-toggle-btn ${type === 'percent' ? 'active' : ''}`}
                                    onClick={() => setType('percent')}
                                >
                                    %
                                </button>
                                <button 
                                    className={`leaderboard-toggle-btn ${type === 'nominal' ? 'active' : ''}`}
                                    onClick={() => setType('nominal')}
                                >
                                    $
                                </button>                    
                            </div>  
                        </div>                               
                    </div>
                    
                    {/* Right Section 2: Body (Main Content Container - Which is So Far Either Grid or Charts) Section */}   
                    <div className="charts-container">
                        <div className="chart-section">
                            {/* Body Section Node 1: Main Content Title/Header */}
                            <h3 className="chart-title">{findOneById(compsCategories, selectedCompsCategoryId).name}</h3>                                
                            {/* Body Section Node 2: Main Content (Chart, Grid, etc) */}
                                { gridLayout  && <MoversGridLayout
                                    orderedCompanyPriceData ={orderedCompanyPriceData}
                                    cpdActiveFields         ={cpdActiveFields}
                                    period                  ={period}
                                    type                    ={type}
                                /> }
                                { barLayout   && <MoversBarLayout
                                    orderedCompanyPriceData ={orderedCompanyPriceData}
                                    cpdActiveFields         ={cpdActiveFields}
                                    type                    ={type}
                                /> }
                        </div>    
                    </div>        

                </div>
            </div>  
        </div>       
    )       
}    

export default Main;
